import { type BranchKey, LayerKey, STANDARD_BRANCH } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Checkbox, Select } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo, useState } from 'react'

import { useMetaModelContext } from '../../../../context/metaModelContext'
import { useTranslateLayer } from '../../../../hooks/useTranslateLayer'
import { collectAllNodesFromTree } from '../../../../uiModels/api/metaModel/helpers'
import { useConfiguredLayers } from '../../../../uiModels/api/metaModel/useConfiguredLayers'
import type { CommonDownloadOptions } from '../../types'

type Props = {
  branchKey: BranchKey
  children: (options: CommonDownloadOptions) => React.ReactNode
  isLayerSelectorVisible: boolean
  isSquashVisible: boolean
  isAllBlocksOptionVisible?: boolean
}

export const CommonDownloadControls = ({
  branchKey,
  children,
  isLayerSelectorVisible,
  isSquashVisible,
  isAllBlocksOptionVisible,
}: Props) => {
  const translate = useTranslate()
  const translateLayer = useTranslateLayer()
  const [
    isIncludeExtrinsicParameterValuesChecked,
    setIsIncludeExtrinsicParameterValuesChecked,
  ] = useState(false)
  const [shouldIncludeBaseBranch, setShouldIncludeBaseBranch] = useState(true)
  const [shouldSquashChangesets, setShouldSquashChangesets] =
    useState(isSquashVisible)
  const [shouldExcludeParameterValues, setShouldExcludeParameterValues] =
    useState(false)
  const [shouldIncludeBaseLayers, setShouldIncludeLowerLayers] = useState(true)
  const [shouldIncludeBaseBlocks, setShouldIncludeLowerBlocks] = useState(true)
  const configuredLayers = useConfiguredLayers()
  const isStandardBranch = branchKey.name === STANDARD_BRANCH
  const { blockTopology } = useMetaModelContext()
  const allBlocks = useMemo(() => {
    if (!blockTopology) {
      return []
    }

    if (isStandardBranch) {
      return [blockTopology.name]
    }

    return collectAllNodesFromTree(blockTopology)
  }, [blockTopology, isStandardBranch])

  // The component only renders if there's configuration on the branch.
  const [layer, setLayer] = useState<LayerKey>(
    configuredLayers[0] ?? new LayerKey({ layerId: null, tenantId: null }),
  )
  const firstBlock = allBlocks.at(0)
  assertNotEmpty(firstBlock, translate('error.noBlocks'))
  const [block, setBlock] = useState<Optional<string>>(
    isAllBlocksOptionVisible ? undefined : firstBlock,
  )

  const isBaseNoLayer = !layer
  const isBaseBlock = block === blockTopology.name

  return (
    <>
      <Select
        wrapperProps={{
          label: translate('branchesTree.export.block.blockLabel'),
        }}
        options={allBlocks}
        onChange={(nextValue) => setBlock(nextValue)}
        value={block}
        shouldShowClearButton={isAllBlocksOptionVisible ?? false}
        placeholder={translate('branchesTree.export.block.allBlocks')}
      />
      {isLayerSelectorVisible && (
        <Select
          wrapperProps={{
            label: translate('branchesTree.export.layer.layerLabel'),
          }}
          options={configuredLayers}
          onChange={(layerOption) => {
            if (layerOption) {
              setLayer(layerOption)
            }
          }}
          renderFn={translateLayer}
          value={layer}
          shouldShowClearButton={false}
        />
      )}
      {branchKey.name === STANDARD_BRANCH ? (
        <Checkbox
          wrapperProps={{
            label: translate(
              'branchesTree.export.tenant.includeExtrinsicParameterValues.label',
            ),
            description: translate(
              'branchesTree.export.tenant.includeExtrinsicParameterValues.description',
            ),
          }}
          checked={
            isIncludeExtrinsicParameterValuesChecked &&
            !shouldExcludeParameterValues
          }
          onChange={setIsIncludeExtrinsicParameterValuesChecked}
          disabled={shouldExcludeParameterValues}
        />
      ) : (
        <Checkbox
          wrapperProps={{
            label: translate('branchesTree.export.includeBaseBranch.label'),
            description: translate(
              'branchesTree.export.includeBaseBranch.description',
            ),
          }}
          checked={shouldIncludeBaseBranch}
          onChange={setShouldIncludeBaseBranch}
        />
      )}
      {isLayerSelectorVisible && (
        <>
          <Checkbox
            wrapperProps={{
              label: translate(
                'branchesTree.export.block.includeBaseBlockConfiguration.label',
              ),
              description: translate(
                'branchesTree.export.block.includeBaseBlockConfiguration.description',
              ),
            }}
            checked={shouldIncludeBaseBlocks && !isBaseBlock}
            onChange={setShouldIncludeLowerBlocks}
            disabled={isBaseBlock}
          />
          <Checkbox
            wrapperProps={{
              label: translate(
                'branchesTree.export.layer.includeBaseLayerConfiguration.label',
              ),
              description: translate(
                'branchesTree.export.layer.includeBaseLayerConfiguration.description',
              ),
            }}
            checked={shouldIncludeBaseLayers && !isBaseNoLayer}
            onChange={setShouldIncludeLowerLayers}
            disabled={isBaseNoLayer}
          />
        </>
      )}
      {isSquashVisible && (
        <Checkbox
          wrapperProps={{
            label: translate('branchesTree.export.squashChangesets.label'),
            description: translate(
              'branchesTree.export.squashChangesets.description',
            ),
          }}
          checked={shouldSquashChangesets && !!block}
          onChange={setShouldSquashChangesets}
          disabled={!block}
        />
      )}
      <Checkbox
        wrapperProps={{
          label: translate('branchesTree.export.excludeParameterValues.label'),
          description: translate(
            'branchesTree.export.excludeParameterValues.description',
          ),
        }}
        checked={shouldExcludeParameterValues}
        onChange={setShouldExcludeParameterValues}
      />
      {children({
        shouldIncludeBaseBranch,
        shouldIncludeExtrinsicParameterValues:
          isIncludeExtrinsicParameterValuesChecked &&
          !shouldExcludeParameterValues,
        shouldSquashChangesets: shouldSquashChangesets && !!block,
        shouldExcludeParameterValues,
        shouldIncludeBaseLayers,
        shouldIncludeBaseBlocks,
        layer,
        block,
      })}
    </>
  )
}
